import { useRef, useEffect } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { POI, MapCanvasRef } from "../../components/MapCanvas";
import type { SheetEntry } from "../../stores/useSheetStack";
import type { VisitorSearchParams } from "../../lib/visitor-search";

interface UseVisitorDeepLinksOptions {
  poiParam: string | undefined;
  viewParam: string | undefined;
  listParam: string | undefined;
  pois: POI[];
  mapData: unknown;
  mapRef: React.RefObject<MapCanvasRef | null>;
  /** Replace the stack with a single entry */
  replace: (entry: SheetEntry) => void;
}

export function useVisitorDeepLinks({
  poiParam,
  viewParam,
  listParam,
  pois,
  mapData,
  mapRef,
  replace,
}: UseVisitorDeepLinksOptions) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as VisitorSearchParams;

  // Legacy redirects (run once on mount). Strip the legacy value after redirect.
  //   ?menu=open          → ?tab=more
  //   ?view=list          → ?tab=search   (keep ?category=)
  //   ?view=events        → ?tab=events
  //   ?view=sponsors      → ?tab=more&view=sponsors
  //   ?view=faq           → ?tab=more&view=faqs
  const searchRef = useRef(search);
  searchRef.current = search;
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rawMenu = params.get("menu");
    const rawView = params.get("view");

    if (rawMenu === "open") {
      void navigate({ to: ".", search: { ...searchRef.current, tab: "more" as const } });
      return;
    }

    if (rawView === "list") {
      void navigate({
        to: ".",
        search: { ...searchRef.current, tab: "search" as const, view: undefined },
      });
      return;
    }

    if (rawView === "events") {
      void navigate({
        to: ".",
        search: { ...searchRef.current, tab: "events" as const, view: undefined },
      });
      return;
    }

    if (rawView === "sponsors") {
      void navigate({
        to: ".",
        search: { ...searchRef.current, tab: "more" as const, view: "sponsors" as const },
      });
      return;
    }

    if (rawView === "faq") {
      void navigate({
        to: ".",
        search: { ...searchRef.current, tab: "more" as const, view: "faqs" as const },
      });
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link: ?poi={slug} — auto-open POI detail sheet + fly to location
  const poiDeepLinkRef = useRef(false);
  useEffect(() => {
    if (poiDeepLinkRef.current || !poiParam || !pois?.length) return;
    const poi = pois.find((p) => p.slug === poiParam || p.id === poiParam);
    if (!poi) return;
    poiDeepLinkRef.current = true;
    replace({ type: "poi", poiId: poi.id, context: "map" });
    if (poi.location) {
      mapRef.current?.flyTo(
        [poi.location.coordinates[0], poi.location.coordinates[1]],
        undefined,
        { top: 0, bottom: window.innerHeight * 0.30, left: 0, right: 0 },
      );
    }
  }, [poiParam, pois, replace, mapRef]);

  // Deep-link: ?list={slug} — auto-open curated list browser
  const curatedDeepLinkRef = useRef(false);
  useEffect(() => {
    if (curatedDeepLinkRef.current || !listParam || !mapData) return;
    curatedDeepLinkRef.current = true;
    replace({ type: "curated" });
  }, [listParam, mapData, replace]);

  // viewParam deep-links (events/sponsors/faq → sheets/fullscreen) were removed
  // in PR2 C2b. Legacy `?view=` values now redirect above; canonical sub-pages
  // (`?tab=more&view=faqs|sponsors`) render inside MorePage.
  void viewParam;
}
